// Modal "Editar datos" de un documento (botón del ícono de lápiz, distinto
// del editor de contenido FileEditor.jsx que abre el botón "Editar").
// Las tres pestañas hablan con el backend real: "Información" actualiza
// metadata (api.update), "Reemplazar archivo" sube un archivo nuevo
// completo vía POST /documents/:id/replace (api.replace, para PDFs/
// imágenes o cualquier reemplazo total), e "Historial" lista las
// versiones guardadas tanto por un reemplazo como por una edición de
// contenido en FileEditor.jsx (ambas comparten la misma colección
// "versions" en el backend).
import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useDocs } from "../context/DocsContext";
import { api } from "../api";
import { X, Save, Upload, FileText, RefreshCw, Edit3, History, Loader, AlertCircle } from "lucide-react";
import { CATEGORIES } from "../constants";
import { formatDateTime } from "../utils/dates";
const TABS = [
  { id: "info",    label: "Información", icon: <Edit3 size={14} /> },
  { id: "file",    label: "Reemplazar archivo", icon: <RefreshCw size={14} /> },
  { id: "history", label: "Historial", icon: <History size={14} /> },
];

const ext  = (name) => name?.split(".").pop().toLowerCase() || "";
const fmtSize = (b) => !b ? "" : b > 1e6 ? `${(b/1e6).toFixed(1)} MB` : `${(b/1e3).toFixed(0)} KB`;

// Solo para previsualizar el próximo número de versión; el valor real lo
// decide el backend (ver bumpVersion en routes/documents.js) al confirmar.
const previewNextVersion = (v) => {
  const match = v?.match(/^v?(\d+)\.(\d+)$/);
  return match ? `v${match[1]}.${parseInt(match[2]) + 1}` : v;
};

export default function EditModal({ doc, onClose }) {
  const { updateDoc, replaceFile } = useDocs();
  const fileRef = useRef();
  const [tab, setTab]   = useState("info");
  const [form, setForm] = useState({
    name: doc.name,
    category: doc.category,
    version: doc.version,
    description: doc.description || "",
  });

  // Tab "Información"
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Tab "Reemplazar archivo"
  const [newFile, setNewFile]         = useState(null);
  const [dragging, setDragging]       = useState(false);
  const [replacing, setReplacing]     = useState(false);
  const [replaced, setReplaced]       = useState(false);
  const [replaceError, setReplaceError] = useState(null);

  // Tab "Historial"
  const [versions, setVersions]             = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  useEffect(() => {
    if (tab !== "history") return;
    setLoadingVersions(true);
    api.getVersions(doc.id)
      .then(v => setVersions((Array.isArray(v) ? v : []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))))
      .finally(() => setLoadingVersions(false));
  }, [tab, doc.id]);

  const handleFile = (f) => {
    if (!f) return;
    setNewFile(f);
    setReplaceError(null);
  };

  const handleSaveInfo = async () => {
    if (!form.description.trim()) {
      setSaveError("La descripcion es obligatoria.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateDoc(doc.id, form);
      if (updated?.error) throw new Error(updated.error);
      setSaved(true);
      setTimeout(() => { setSaved(false); onClose(); }, 1000);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReplaceFile = async () => {
    if (!newFile) return;
    setReplacing(true);
    setReplaceError(null);
    try {
      const fd = new FormData();
      fd.append("file", newFile);
      const updated = await replaceFile(doc.id, fd);
      if (updated?.error) throw new Error(updated.error);
      setForm(p => ({ ...p, version: updated.version }));
      setNewFile(null);
      setReplaced(true);
      setTimeout(() => { setReplaced(false); onClose(); }, 1200);
    } catch (e) {
      setReplaceError(e.message);
    } finally {
      setReplacing(false);
    }
  };

  return (
    <motion.div className="modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <motion.div className="modal" style={{ maxWidth: 560 }}
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}>

        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>Editar documento</h2>
            <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 2 }}>{doc.name}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--border)", padding: "0 24px" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 14px", border: "none", background: "transparent",
                fontFamily: "inherit", fontSize: "0.78rem", cursor: "pointer",
                color: tab === t.id ? "var(--primary)" : "var(--text-muted)",
                borderBottom: tab === t.id ? "2px solid var(--primary)" : "2px solid transparent",
                fontWeight: tab === t.id ? 600 : 400,
                marginBottom: -1,
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Tab: Información */}
        {tab === "info" && (
          <div className="modal-form" style={{ padding: "20px 24px 8px" }}>
            <div className="form-row">
              <label>Nombre *</label>
              <input className="form-input" value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-row">
              <label>Carpeta</label>
              <select className="form-input" value={form.category}
                onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>Versión</label>
              <input className="form-input" value={form.version}
                onChange={e => setForm(p => ({ ...p, version: e.target.value }))} />
            </div>
            <div className="form-row">
              <label>Descripción *</label>
              <textarea className="form-input form-textarea" rows={4} value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            {saveError && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem" }}>
                <AlertCircle size={15} /> {saveError}
              </div>
            )}
          </div>
        )}

        {/* Tab: Reemplazar archivo */}
        {tab === "file" && (
          <div style={{ padding: "20px 24px 8px" }}>
            <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.6 }}>
              Sube la nueva versión del archivo. La versión actual (<strong>{form.version}</strong>) quedará guardada en el historial y la versión se incrementará a <strong>{previewNextVersion(form.version)}</strong>.
            </p>

            <div
              className={`dropzone ${dragging ? "dragging" : ""} ${newFile ? "has-file" : ""}`}
              onClick={() => fileRef.current.click()}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
            >
              <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.docx,.doc,.png,.jpg" hidden
                onChange={e => handleFile(e.target.files[0])} />
              {newFile ? (
                <>
                  <FileText size={32} style={{ color: "var(--green)" }} />
                  <p style={{ fontWeight: 600, color: "var(--text)" }}>{newFile.name}</p>
                  <p className="drop-sub">{fmtSize(newFile.size)} · listo para guardar</p>
                </>
              ) : (
                <>
                  <Upload size={32} className="drop-icon" />
                  <p>Arrastra el nuevo archivo aquí</p>
                  <p className="drop-sub">PDF, Excel, Word, Imágenes</p>
                </>
              )}
            </div>

            {newFile && !replaceError && (
              <div style={{ marginTop: 12, padding: "10px 14px", background: "#F0FDF4", borderRadius: 8, border: "1px solid #BBF7D0", fontSize: "0.78rem", color: "#166534" }}>
                Se guardará como versión <strong>{previewNextVersion(form.version)}</strong> al confirmar.
              </div>
            )}

            {replaceError && (
              <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem" }}>
                <AlertCircle size={15} /> {replaceError}
              </div>
            )}
          </div>
        )}

        {/* Tab: Historial */}
        {tab === "history" && (
          <div style={{ padding: "20px 24px 8px", maxHeight: 320, overflowY: "auto" }}>
            {/* Versión actual */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#F0FDFA", borderRadius: 8, border: "1px solid #99E8DE", marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: "0.8rem", fontWeight: 600, color: "#065F51" }}>{form.version} — actual</p>
                <p style={{ fontSize: "0.72rem", color: "var(--primary)", marginTop: 2 }}>
                  {ext(doc.filename).toUpperCase()} · {fmtSize(doc.size)}
                </p>
              </div>
              <a href={api.fileUrl(doc.filename)} download={doc.originalname}
                style={{ fontSize: "0.72rem", color: "#065F51", textDecoration: "none", fontWeight: 500 }}>
                Descargar
              </a>
            </div>

            {/* Versiones anteriores */}
            {loadingVersions ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 0", color: "var(--text-muted)", fontSize: "0.8rem" }}>
                <Loader size={14} className="spin" /> Cargando historial...
              </div>
            ) : versions.length === 0 ? (
              <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", textAlign: "center", padding: "20px 0" }}>
                Aún no hay versiones anteriores.<br/>Al reemplazar o editar el archivo aparecerán aquí.
              </p>
            ) : (
              versions.map(v => (
                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "var(--bg)", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text)" }}>{v.version}</p>
                    {v.change_description && (
                      <p style={{ fontSize: "0.74rem", color: "var(--text)", marginTop: 3 }}>{v.change_description}</p>
                    )}
                    <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 2 }}>
                      {formatDateTime(v.created_at)} · {fmtSize(v.size)}
                    </p>
                  </div>
                  <a href={api.versionFileUrl(v.filename)} download={v.originalname}
                    style={{ fontSize: "0.72rem", color: "var(--primary)", textDecoration: "none", fontWeight: 500 }}>
                    Descargar
                  </a>
                </div>
              ))
            )}
          </div>
        )}

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          {tab === "info" && (
            <button className="btn-primary" onClick={handleSaveInfo} disabled={saving || !form.description.trim()}
              style={{ background: saved ? "#22863a" : undefined }}>
              {saving ? <><Loader size={15} className="spin" /> Guardando...</> : saved ? "✓ Guardado" : <><Save size={15} /> Guardar cambios</>}
            </button>
          )}
          {tab === "file" && (
            <button className="btn-primary" onClick={handleReplaceFile} disabled={!newFile || replacing}
              style={{ background: replaced ? "#22863a" : undefined }}>
              {replacing ? <><Loader size={15} className="spin" /> Subiendo...</> : replaced ? "✓ Reemplazado" : <><Upload size={15} /> Reemplazar archivo</>}
            </button>
          )}
        </div>

      </motion.div>
    </motion.div>
  );
}
